import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import { useJsApiLoader, Autocomplete, GoogleMap, Marker } from '@react-google-maps/api';
import { UI, DISPLAY } from '../../styles/tl5';
import { useGetAppSettingsQuery } from '../../store/siteContentApi';
import { PinIcon, CloseIcon, MapIcon } from './TlIcons';

// Address search + "use my current location", sitting above the booking
// form's three address fields and filling them in.
//
// The reference's Details step is three plain inputs and nothing else.
// Typing a full address on a phone, at 7am, to get a blood test, is the
// worst part of that flow — so this adds Google Places search and a GPS
// button on top, and the three fields stay exactly where they were,
// auto-filled and still editable. Nothing is taken away.
//
// What it emits (onPick): { address, lat, lng, accuracy, place_id, and
// the parsed parts }. That is the shape `patient.location` already takes
// in the Booking model and in bookingController — the fields were there
// waiting, unused, because the old LocationPicker was dropped.
//
// Degrading: the Maps *script* loads fine even when the project has no
// billing — it is the API calls that come back REQUEST_DENIED. So
// `isLoaded` alone is not enough to know search will work. On mount this
// fires one throwaway Places query to find out.
//
// When it comes back denied the block still renders, because a missing
// button is its own kind of broken. Instead the search input is disabled
// and says so, and **Use my location still works**: the browser gives us
// coordinates without Google, and coordinates alone are what the
// Trustian actually navigates on. Only turning those coordinates into
// street text needs the Geocoding API. So the patient types their
// address as before, and the pin still reaches the collector.
//
// NOTE: billing is currently disabled on the Google Cloud project, so
// both Places and Geocoding return REQUEST_DENIED and this block stays
// hidden. It starts working the moment billing is enabled; no code
// change needed. API-NOTES 2.47.

const LIBRARIES = ['places'];
const DEFAULT_CENTRE = { lat: 17.4399, lng: 78.4983 }; // Begumpet
const ENV_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Walks address_components[] into the flat shape the booking stores.
const extractParts = (components = []) => {
  const get = (type, short = false) => {
    const c = components.find((x) => x.types?.includes(type));
    if (!c) return '';
    return short ? c.short_name : c.long_name;
  };
  return {
    street_number: get('street_number'),
    street: get('route'),
    sublocality: get('sublocality_level_1') || get('sublocality') || get('neighborhood'),
    area: get('sublocality_level_2') || get('administrative_area_level_3'),
    city: get('locality') || get('administrative_area_level_2'),
    district: get('administrative_area_level_2'),
    state: get('administrative_area_level_1'),
    country: get('country'),
    country_code: get('country', true),
    pincode: get('postal_code'),
  };
};

const reverseGeocode = (lat, lng) => new Promise((resolve) => {
  const g = window.google;
  if (!g?.maps?.Geocoder) return resolve(null);
  new g.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
    if (status === 'OK' && results?.[0]) {
      resolve({
        formatted_address: results[0].formatted_address,
        place_id: results[0].place_id,
        parts: extractParts(results[0].address_components),
      });
    } else {
      resolve(null);
    }
  });
  return undefined;
});

const searchField = {
  flex: 1,
  minWidth: 0,
  border: '1.5px solid var(--tl-line)',
  borderRadius: '14px',
  outline: 0,
  font: 'inherit',
  fontFamily: UI,
  fontSize: 16,
  p: '13px 14px',
  color: 'var(--tl-g9)',
  bgcolor: '#FFFFFF',
  width: '100%',
  transition: 'border-color .2s',
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const actionBtn = (busy) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexShrink: 0,
  border: '1.5px solid var(--tl-teal)',
  borderRadius: '14px',
  p: '13px 18px',
  font: 'inherit',
  fontFamily: UI,
  fontSize: 15,
  fontWeight: 800,
  cursor: busy ? 'default' : 'pointer',
  bgcolor: busy ? 'var(--tl-mint)' : '#FFFFFF',
  color: 'var(--tl-teal-d)',
  whiteSpace: 'nowrap',
  transition: 'background-color .2s',
  '&:not(:disabled):hover': { bgcolor: 'var(--tl-mint)' },
});

const AddressAutofillInner = ({ apiKey, onPick }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });
  const acRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // null = probing, true/false = Places answered
  const [usable, setUsable] = useState(null);
  // The "pick on map" dialog: a draggable pin the patient drops on their
  // own door. Gates and lanes rarely match the street address, and this
  // is the only way a Trustian finds the right one.
  const [mapOpen, setMapOpen] = useState(false);
  const [pin, setPin] = useState(null);   // { lat, lng, address, place_id, parts }
  const mapRef = useRef(null);

  // One throwaway prediction request to find out whether this key can
  // actually serve Places, rather than trusting that the script loaded.
  useEffect(() => {
    if (!isLoaded) return;
    const svc = window.google?.maps?.places?.AutocompleteService;
    if (!svc) { setUsable(false); return; }
    try {
      new svc().getPlacePredictions(
        { input: 'Hyderabad', componentRestrictions: { country: 'in' } },
        (_r, status) => {
          const S = window.google.maps.places.PlacesServiceStatus;
          setUsable(status === S.OK || status === S.ZERO_RESULTS);
        },
      );
    } catch (e) {
      setUsable(false);
    }
  }, [isLoaded]);

  const onPlaceChanged = useCallback(() => {
    const place = acRef.current?.getPlace?.();
    if (!place?.geometry?.location) {
      setErr('Pick an address from the list so we can find it on the map');
      return;
    }
    setErr('');
    onPick({
      address: place.formatted_address || place.name || '',
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      accuracy: null,
      place_id: place.place_id || null,
      ...extractParts(place.address_components),
    });
  }, [onPick]);

  // Dropping or dragging the pin: resolve it to an address as we go so
  // the patient can see where they have actually put it.
  const movePin = useCallback(async (lat, lng) => {
    setPin({ lat, lng, address: 'Finding address…', place_id: null, parts: {} });
    const geo = await reverseGeocode(lat, lng);
    setPin({
      lat,
      lng,
      address: geo?.formatted_address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
      place_id: geo?.place_id || null,
      parts: geo?.parts || {},
    });
  }, []);

  const openMap = useCallback(() => {
    setErr('');
    setMapOpen(true);
    // Centre on the patient if they will let us, otherwise on the lab.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => movePin(pos.coords.latitude, pos.coords.longitude),
        () => movePin(DEFAULT_CENTRE.lat, DEFAULT_CENTRE.lng),
        { enableHighAccuracy: true, timeout: 8000 },
      );
    } else {
      movePin(DEFAULT_CENTRE.lat, DEFAULT_CENTRE.lng);
    }
  }, [movePin]);

  const confirmPin = useCallback(() => {
    if (pin) {
      onPick({
        address: pin.address,
        lat: pin.lat,
        lng: pin.lng,
        accuracy: null, // dropped by hand, not GPS
        place_id: pin.place_id,
        ...(pin.parts || {}),
      });
    }
    setMapOpen(false);
  }, [pin, onPick]);

  const useCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setErr('This browser can’t share your location');
      return;
    }
    setErr('');
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        const geo = await reverseGeocode(lat, lng);
        if (!geo) {
          setBusy(false);
          setErr('Found you, but couldn’t read the address — please type it in');
          return;
        }
        onPick({
          address: geo.formatted_address,
          lat,
          lng,
          accuracy,
          place_id: geo.place_id,
          ...geo.parts,
        });
        setBusy(false);
      },
      (e) => {
        setBusy(false);
        setErr(
          e.code === 1 ? 'Location permission denied — allow it, or type your address below'
            : e.code === 2 ? 'Couldn’t detect your location'
              : 'Finding your location took too long',
        );
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  }, [onPick]);

  // Script failed, still probing, or Places refused us: show nothing and
  // let the manual fields below do their job.
  // Only the script failing takes the whole block away — without
  // google.maps there is no Autocomplete component to render at all.
  if (loadError || !isLoaded) return null;
  const placesOk = usable === true;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
        {placesOk ? 'Search your address, or use your location' : 'Use your location, or type your address below'}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ flex: '1 1 240px', minWidth: 0, ...(placesOk ? null : { opacity: 0.55, pointerEvents: 'none' }) }}>
          <Autocomplete
            onLoad={(ac) => { acRef.current = ac; }}
            onPlaceChanged={onPlaceChanged}
            options={{
              componentRestrictions: { country: 'in' },
              fields: ['formatted_address', 'geometry.location', 'address_components', 'place_id', 'name'],
            }}
          >
            <Box
              component="input"
              type="text"
              placeholder={placesOk ? 'Start typing your address…' : 'Address search is being set up'}
              disabled={!placesOk}
              aria-label="Search your address"
              sx={searchField}
            />
          </Autocomplete>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={useCurrentLocation}
          disabled={busy}
          sx={actionBtn(busy)}
        >
          <PinIcon size={16} />
          {busy ? 'Finding you…' : 'Use my location'}
        </Box>
        <Box component="button" type="button" onClick={openMap} sx={actionBtn(false)}>
          <MapIcon size={16} />
          Pick on map
        </Box>
      </Box>
      {err && (
        <Box component="span" role="alert" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: '#B23B37' }}>
          {err}
        </Box>
      )}

      {/* ── pick-on-map dialog ──────────────────────────────────────── */}
      <Dialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '28px',
            overflow: 'hidden',
            bgcolor: '#FFFFFF',
            m: 2,
            width: 'calc(100% - 32px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 20px', bgcolor: 'var(--tl-g9)', color: '#FFFFFF' }}>
          <Box component="span" sx={{ flex: 1, fontFamily: DISPLAY, fontWeight: 700, fontSize: 20, letterSpacing: '-.01em' }}>
            Drop a pin on your door
          </Box>
          <Box
            component="button"
            type="button"
            aria-label="Close"
            onClick={() => setMapOpen(false)}
            sx={{
              width: 38, height: 38, flexShrink: 0, display: 'grid', placeItems: 'center',
              border: 0, borderRadius: '50%', cursor: 'pointer',
              bgcolor: 'rgba(255,255,255,.1)', color: '#FFFFFF',
              '&:hover': { bgcolor: 'rgba(255,255,255,.2)' },
            }}
          >
            <CloseIcon size={18} />
          </Box>
        </Box>

        <Box sx={{ height: { xs: 300, sm: 400 }, bgcolor: '#D5EFE8' }}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={pin ? { lat: pin.lat, lng: pin.lng } : DEFAULT_CENTRE}
            zoom={pin ? 17 : 12}
            onLoad={(m) => { mapRef.current = m; }}
            onClick={(e) => movePin(e.latLng.lat(), e.latLng.lng())}
            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
          >
            {pin && (
              <Marker
                position={{ lat: pin.lat, lng: pin.lng }}
                draggable
                onDragEnd={(e) => movePin(e.latLng.lat(), e.latLng.lng())}
              />
            )}
          </GoogleMap>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, p: 'clamp(18px, 3vw, 24px)' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--tl-teal-d)' }}>
              Pin is here
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.5, color: 'var(--tl-g9)' }}>
              {pin ? pin.address : 'Tap the map, or drag the pin, to set your exact spot.'}
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={confirmPin}
            disabled={!pin}
            sx={{
              alignSelf: 'start',
              border: 0, borderRadius: '16px', p: '14px 22px',
              font: 'inherit', fontFamily: UI, fontSize: 16, fontWeight: 800,
              cursor: pin ? 'pointer' : 'default',
              bgcolor: pin ? 'var(--tl-g9)' : 'var(--tl-line)',
              color: pin ? '#FFFFFF' : 'var(--tl-muted-2)',
            }}
          >
            Use this location
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

const AddressAutofill = ({ onPick }) => {
  const { data: settings } = useGetAppSettingsQuery();
  const apiKey = settings?.google_maps_api_key || ENV_KEY;
  if (!apiKey) return null;
  return <AddressAutofillInner apiKey={apiKey} onPick={onPick} />;
};

export default AddressAutofill;
